import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { weightLossRefillsBMIValidation } from '@api';
import {
  ArrowRightIcon,
  Button,
  ErrorMessage,
  Field,
  Input,
  Label,
  Paragraph,
} from '@quickmd-llc/design-system';
import { useRefillCheckup } from '../hooks/useRefillCheckup';

type FormValues = {
  heightFeet: number;
  heightInches: number;
  weightPounds: number;
};

export function WeightForm() {
  const { nextSlide, toggleCheckupFailed } = useRefillCheckup();
  const { formState, handleSubmit, register } = useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const { errors } = formState;
  const errorWeightMessage = errors?.weightPounds?.message;
  const errorHeightFeetMessage = errors?.heightFeet?.message;
  const errorHeightInchesMessage = errors?.heightInches?.message;

  async function onSubmit(data: FormValues) {
    const totalHeightInches = getTotalHeightInches(data);

    try {
      setLoading(true);

      const { isBodyMassIndexInRange } = await weightLossRefillsBMIValidation({
        heightInches: totalHeightInches,
        weightPounds: data.weightPounds,
      });

      if (isBodyMassIndexInRange) {
        nextSlide();
      } else {
        toggleCheckupFailed();
      }
    } catch {
      toggleCheckupFailed();
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 flex-col">
      <div>
        <Field className="mb-6" error={Boolean(errorWeightMessage)}>
          <Label>Weight</Label>

          {errorWeightMessage && (
            <ErrorMessage>{errorWeightMessage}</ErrorMessage>
          )}

          <Input
            {...register('weightPounds', weightOptions)}
            step={1}
            pattern={'/^[0-9]+$/'}
            placeholder="0"
            type="number"
            disableNativeNumericControls
            rightSideLabel={
              <Paragraph className="text-shade-70" size="lg">
                lbs.
              </Paragraph>
            }
          />
        </Field>

        <div className="mt-2 flex gap-2">
          <Field className="flex-1" error={Boolean(errorHeightFeetMessage)}>
            <Label>Height</Label>

            {errorHeightFeetMessage && (
              <ErrorMessage>{errorHeightFeetMessage}</ErrorMessage>
            )}

            <Input
              {...register('heightFeet', heightFeetOptions)}
              step={1}
              pattern={'/^[0-9]+$/'}
              placeholder="0"
              type="number"
              disableNativeNumericControls
              rightSideLabel={
                <Paragraph className="text-shade-70" size="lg">
                  ft.
                </Paragraph>
              }
            />
          </Field>

          <Field className="flex-1" error={Boolean(errorHeightInchesMessage)}>
            <Label>&nbsp;</Label>

            {errorHeightInchesMessage && (
              <ErrorMessage>{errorHeightInchesMessage}</ErrorMessage>
            )}

            {errorHeightFeetMessage && !errorHeightInchesMessage && (
              <ErrorMessage>&nbsp;</ErrorMessage>
            )}

            <Input
              {...register('heightInches', heightInchesOptions)}
              step={1}
              pattern={'/^[0-9]+$/'}
              placeholder="0"
              type="number"
              disableNativeNumericControls
              rightSideLabel={
                <Paragraph className="text-shade-70" size="lg">
                  in.
                </Paragraph>
              }
            />
          </Field>
        </div>
      </div>

      <Button className="mt-auto w-full" loading={loading} type="submit">
        Next
        <ArrowRightIcon />
      </Button>
    </form>
  );
}

function getTotalHeightInches(data: FormValues) {
  const { heightFeet, heightInches } = data;
  return heightFeet * 12 + heightInches;
}

const weightOptions = {
  min: { value: 0, message: 'Please enter a valid weight to continue.' },
  required: { value: true, message: 'Please enter your weight to continue.' },
  valueAsNumber: true,
};

const heightFeetOptions = {
  required: { value: true, message: 'Please enter your height to continue.' },
  valueAsNumber: true,
};

const heightInchesOptions = {
  max: { value: 12, message: 'Please enter a valid height to continue.' },
  setValueAs: (value: string) => (value.trim() === '' ? 0 : Number(value)),
};
